import getAxios from 'apiRestConnector';
import { CandidateInterface } from 'components/models/CandidateInterfaces';
import { DatosInterface } from 'components/models/PersonalInformationInterfaces';
import { AlertColor } from '@mui/material';

import React, { useContext, useEffect, useState, useRef } from 'react';
import { AuthContext } from 'services/authContext/AuthContextProvider';
import PersonalInfoHeadItem from './personalInfoItem/PersonalInfoHeadItem';
import PersonalFieldForm from './personalFieldForm/PersonalFieldForm';
import { getDifference } from '../../../../helpers/helpers';
import { Box, TextField, Select, MenuItem, FormControl, InputLabel, Tooltip, IconButton } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

interface Props {
  dataCandidate: CandidateInterface;
  skillsTemplateDatos: DatosInterface[];
  solicitudCandidate: number[];
  celular: number;
  onChangeData: (data: CandidateInterface) => void;
  onTogglePrivado: (nombreDato: string) => void;
  setTelefono: (telefono: string) => void;
  setEmail: (email: string) => void;
  setNombre: (nombre: string) => void;
}

const { REACT_APP_BACKEND_URL } = process.env;
const URL_BACKEND = `${REACT_APP_BACKEND_URL}/templates/skills/`;
const PersonalInfoComp: React.FC<Props> = ({ dataCandidate, skillsTemplateDatos, celular, onChangeData, onTogglePrivado }) => {
  const { storeAuth } = useContext(AuthContext)!;
  const { access_token } = storeAuth.user;
  const [skeleton, setSkeleton] = useState<CandidateInterface | null>(null);
  const initialData = useRef(dataCandidate);
  const [message] = useState<{
    message: string;
    severity: AlertColor;
  }>({
    message: '',
    severity: 'error',
  });
  const [changeValidate, setChangeValidate] = React.useState<{ name: string; validate: boolean }[]>([]);
  const [uploadFields, setUploadFields] = useState<Record<string, string>>({});

  useEffect(() => {
    getAxios
      .instance('')
      .get(URL_BACKEND, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      })
      .then((result: any) => setSkeleton(result.data))
      .catch((error) => console.error('error fetching skeleton:', error.message));
  }, [access_token]);

  useEffect(() => {
    if (skeleton?.datos?.length && dataCandidate?.datos) {
      const currentDataNombres = new Set(dataCandidate.datos.map(d => d.nombre));
      const differentElements = skeleton.datos.filter(skelDato => !currentDataNombres.has(skelDato.nombre));
      
      if (differentElements.length > 0) {
        const result = structuredClone(dataCandidate);
        result.datos = [...result.datos, ...differentElements];
        onChangeData(result);
      }
    }
  }, [skeleton, dataCandidate, onChangeData]);

  const handleValueChange = (nombreCampo: string, nuevoValor: any) => {
    const result = structuredClone(dataCandidate);
    let updated = false;
    result.datos = result.datos?.map((dato) => {
      if (dato.nombre === nombreCampo) {
        updated = true;
        return { ...dato, valor: nuevoValor };
      }
      return dato;
    });

    if (!updated && result.datos) {
        const skelData = skeleton?.datos?.find(d => d.nombre === nombreCampo);
        result.datos.push({
            nombre: nombreCampo,
            valor: nuevoValor,
            privado: skelData?.privado ?? false,
            ...(skelData?.type && { type: skelData.type })
        });
    }

    onChangeData(result);
  };

  const onAutocompleteChange = (nombre: string) => (_event: any, newValue: any) => {
    const finalValue = (nombre === 'Moneda Renta Actual' || nombre === 'Moneda Pretenciones de Renta' || nombre === 'Moneda Tarifa') 
                       ? newValue?.typeCoin
                       : newValue;
    handleValueChange(nombre, finalValue);
    setChangeValidate(prev => 
        prev.some(v => v.name === nombre) 
            ? prev.map(v => v.name === nombre ? { ...v, validate: true } : v)
            : [...prev, { name: nombre, validate: true }]
    );
  };

  const handleFiles = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const name = target.name;
      const auxTemplate = structuredClone(dataCandidate);

      if (typeof target.files !== 'object' || target.files === null) {
        return;
      }

      const files = target.files;
      if (!files.length) return;
      if (
        ![
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ].includes(files[0].type)
      ) {
        alert('El archivo debe ser un PDF o un documento de Word.');
        return;
      }

      const baseUrl = `${REACT_APP_BACKEND_URL}/personas/${celular}/file/${name}`;
      const file = new FormData();
      file.append('file', files[0]);
      const response = await fetch(baseUrl, {
        method: 'POST',
        body: file,
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });
      const result = await response.json();

      auxTemplate.datos = dataCandidate.datos.map((data: DatosInterface) => {
        if (data.nombre === name) {
          const type = skeleton?.datos?.find(
            ({ nombre: n }: DatosInterface) => n === data.nombre
          )?.type;
          setUploadFields((prevState) => ({ ...prevState, [name]: result.url }));
          return { ...data, valor: result.url, ...(type && { type }) };
        }
        return data;
      });
      onChangeData(auxTemplate);
    } catch (error) {}
  };

  const handleLink = (urlString: string) => {
    if (!urlString) return;
    try { 
        window.open(new URL(urlString).href); 
    } catch (error) { console.error('URL inválida:', urlString); }
  };

  const renderLockIcon = (dato: DatosInterface) => {
    if (dato.privado === undefined) return null;
    return (
      <Tooltip title={dato.privado ? "Marcar como público" : "Marcar como privado"}>
        <span>
          <IconButton
            size="small"
            onClick={() => onTogglePrivado(dato.nombre)}
            disabled={!dato.nombre}
            sx={{ cursor: dato.nombre ? 'pointer' : 'default' }}
          >
            {dato.privado ? <LockIcon fontSize="inherit" /> : <LockOpenIcon fontSize="inherit" />}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const datosParaRenderizar = dataCandidate?.datos ? [...dataCandidate.datos] : [];
  const nombresExistentes = new Set(datosParaRenderizar.map(d => d.nombre));

  if (!nombresExistentes.has('Tarifa')) {
      datosParaRenderizar.push({ nombre: 'Tarifa', valor: 1000, privado: false });
  }
  if (!nombresExistentes.has('Moneda Tarifa')) {
      datosParaRenderizar.push({ nombre: 'Moneda Tarifa', valor: 'CLP', privado: false });
  }

  return (
    <Box>
      {datosParaRenderizar.map((dato: DatosInterface, index: number) => {
        if (dato.nombre === 'Tarifa') {
          return (
            <Box key={index + dato.nombre} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              {renderLockIcon(dato)}
              <Box sx={{ flexGrow: 1 }}>
                <TextField
                  label="Tarifa"
                  type="number"
                  value={dato.valor ?? ''}
                  onChange={(e) => handleValueChange(dato.nombre, e.target.value ? Number(e.target.value) : '')}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </Box>
            </Box>
          );
        }

        if (dato.nombre === 'Moneda Tarifa') {
          return (
            <Box key={index + dato.nombre} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              {renderLockIcon(dato)}
              <Box sx={{ flexGrow: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Moneda Tarifa</InputLabel>
                  <Select
                    value={dato.valor ?? 'CLP'}
                    label="Moneda Tarifa"
                    onChange={(e) => handleValueChange(dato.nombre, e.target.value)}
                  >
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                    <MenuItem value="CLP">CLP</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          );
        }

        if (dato.nombre !== 'Tarifa' && dato.nombre !== 'Moneda Tarifa' && dato.nombre !== 'Nombre' && dato.nombre !== 'Correo') {
          return (
            <PersonalInfoHeadItem 
              key={index + dato.nombre} 
              isPrivate={dato?.privado ?? false}
              nombreDato={dato.nombre}
              onTogglePrivado={onTogglePrivado}
            >
              <PersonalFieldForm
                index={index}
                handleFiles={handleFiles}
                handleLink={handleLink}
                celular={celular}
                message={message}
                data={dato}
                uploadFields={uploadFields}
                changeValidate={changeValidate}
                templateData={dataCandidate.datos}
                skillsTemplateDatos={skillsTemplateDatos}
                initial={initialData.current.datos}
                onAutocompleteChange={onAutocompleteChange(dato.nombre)}
                onChangeValue={(newValue) => handleValueChange(dato.nombre, newValue)}
              />
            </PersonalInfoHeadItem>
          );
        }
        return null;
      })}
    </Box>
  );
};

export default PersonalInfoComp;
